import { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton
} from '@mui/material';

// Example icons
import SportsIcon from '@mui/icons-material/Sports';
import ProductionQuantityLimitsIcon from '@mui/icons-material/ProductionQuantityLimits';
import CategoryIcon from '@mui/icons-material/Category';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import MailLockIcon from '@mui/icons-material/MailLock';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';

import Iconify from './Iconify';
import { useUser } from '../UserContext';

import MemberNavSection from '../members/MemberNavSection';


// --------------------------------------
// Common + Admin/Staff menu items
// --------------------------------------
const commonMenu = [
  {
    title: 'Dashboard',
    path: '/dashboard/app',
    icon: <SpaceDashboardIcon />
  },
  {
    title: 'Member Home',
    path: '/dashboard/member-home',
    icon: <HomeOutlinedIcon />
  },
  {
    title: 'Calendar',
    path: '/dashboard/calendar',
    icon: <CalendarMonthIcon />
  }
];

const staffAdminMenu = [
  {
    title: 'Members',
    path: '/dashboard/members',
    icon: <PeopleAltIcon />
  },
  {
    title: 'Plans',
    path: '/dashboard/plans',
    icon: <FormatListNumberedIcon />
  },
  {
    title: 'Pennants',
    path: '/dashboard/pennants',
    icon: <SportsTennisIcon />
  },
  {
    title: 'Run Pennant',
    path: '/dashboard/run-pennant',
    icon: <DirectionsRunIcon />
  },
  {
    title: 'Ladders',
    path: '/dashboard/ladder',
    icon: <LeaderboardIcon />
    
  },
  {
    title: 'Draws',
    path: '/dashboard/draws',
    icon: <CalendarViewWeekIcon />
    
  },
  {
    title: 'Products',
    path: '/dashboard/products',
    icon: <ProductionQuantityLimitsIcon />
  },
  {
    title: 'Courts Calendar',
    path: '/dashboard/courts-calendar',
    icon: <CalendarMonthIcon />
  },
  {
    title: 'Manage Users',
    path: '/dashboard/manage-users',
    icon: <AdminPanelSettingsIcon />
  },
  {
    title: 'Email Templates',
    path: '/dashboard/email-template-editor',
    icon: <MailLockIcon />
  },
  {
    title: 'Website Management',
    path: '/dashboard/posts',
    icon: <LanguageIcon />
  },
  {
    title: 'Access Control',
    path: '/dashboard/access-control',
    icon: <LanguageIcon />
  },
  {
    title: 'Card Manager',
    path: '/dashboard/card-manager',
    icon: <LanguageIcon />
  }


];

// -------------
// Styled items
// (same as original code)
// -------------
const ListItemStyle = styled((props) => <ListItemButton disableGutters {...props} />)(
  ({ theme }) => ({
    ...theme.typography.body2,
    height: 48,
    position: 'relative',
    textTransform: 'capitalize',
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(2.5),
    color: theme.palette.text.secondary,
    '&:before': {
      top: 0,
      right: 0,
      width: 3,
      bottom: 0,
      content: "''",
      display: 'none',
      position: 'absolute',
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      backgroundColor: theme.palette.primary.main
    }
  })
);

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

function NavItem({ item, active }) {
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => setOpen((prev) => !prev);

  const activeRootStyle = {
    color: 'primary.main',
    fontWeight: 'fontWeightMedium',
    bgcolor: (theme) => alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
    '&:before': { display: 'block' }
  };

  const activeSubStyle = {
    color: 'text.primary',
    fontWeight: 'fontWeightMedium'
  };

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle)
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Iconify
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => {
              const { title, path } = child;
              const isActiveSub = active(path);

              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle)
                  }}
                >
                  <ListItemIconStyle>
                    <Box
                      component="span"
                      sx={{
                        width: 4,
                        height: 4,
                        display: 'flex',
                        borderRadius: '50%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'text.disabled',
                        transition: (theme) => theme.transitions.create('transform'),
                        ...(isActiveSub && {
                          transform: 'scale(2)',
                          bgcolor: 'primary.main'
                        })
                      }}
                    />
                  </ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle)
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func
};

/**
 * NavSection:
 *  - If user is admin/staff => show commonMenu + staffAdminMenu
 *  - If user is a member => show just the MemberNavSection (or do something else)
 */
export default function NavSection() {
  const { pathname } = useLocation();
  const match = (path) => (path ? !!matchPath({ path, end: false }, pathname) : false);

  const { user } = useUser();
  const role = user?.role || 'guest';

  // staff/admin get these
  const staffAdminConfig = [...commonMenu, ...staffAdminMenu];

  if (role === 'member') {
    // If you want to ONLY show the separate MemberNavSection, do so:
    return <MemberNavSection />;
  }

  // Otherwise (admin/staff/guest):
  return (
    <Box>
      <List disablePadding>
        {staffAdminConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </List>
    </Box>
  );
}
